import { useEffect, useState } from 'react'
import { ensureAnonSession } from '../lib/api/tv'
import { readError } from '../lib/errors'
import { ThreeStepTvHomeScreen } from './ThreeStepTvHomeScreen'
import { ThreeStepTvRoomScreen } from './ThreeStepTvRoomScreen'
import { useT } from '../i18n'

/**
 * Three Step Solution TV entrypoint.
 *
 * Mirrors `ColorfulTvApp` — the TV is a pure display, no controls, no
 * sign-in. Whoever wants to project a game does it from the main app
 * on their phone (where they create the room and host); they then
 * type the code into /tv/three-step on the big screen.
 *
 * Paths:
 *   /tv/three-step             → landing with a code-entry form
 *   /tv/three-step/ABCDEF      → the read-only room display
 */
export function ThreeStepTvApp() {
  const t = useT()
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const code = parseRoomCode(window.location.pathname)

  useEffect(() => {
    let cancelled = false
    // Anonymous session so Realtime subscriptions + table reads work.
    // No display name passed — see ColorfulTvApp for the rationale
    // (don't overwrite a real signed-in profile if both surfaces share
    // the same browser).
    ensureAnonSession()
      .then(() => { if (!cancelled) setReady(true) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  if (error) return <TvMessage title={t("Couldn't start the TV session")} body={error} />
  if (!ready) return <TvMessage title={t('Starting up…')} body={t('Connecting.')} />

  if (code) return <ThreeStepTvRoomScreen code={code} />
  return <ThreeStepTvHomeScreen />
}

function parseRoomCode(path: string): string | null {
  const m = path.match(/^\/tv\/three-step\/?([A-Za-z0-9]+)?\/?$/)
  const raw = m?.[1]
  if (!raw) return null
  return raw.toUpperCase()
}

function TvMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-text p-12">
      <div className="text-center max-w-3xl">
        <h1 className="font-display text-5xl font-bold mb-4">{title}</h1>
        <p className="text-xl text-brand-muted">{body}</p>
      </div>
    </div>
  )
}
